class CartUpsellPopup {
  constructor() {
    this.popup = document.getElementById('cart-upsell-popup');
    if (!this.popup) return;

    // Move the popup to be a direct child of <body> so it escapes the
    // header's stacking context (otherwise the sticky navbar can render on top).
    if (this.popup.parentElement !== document.body) {
      document.body.appendChild(this.popup);
    }

    this.config = this.readConfig();
    this.strings = this.config.strings || {};

    this.addedProductContainer = this.popup.querySelector('[data-added-product]');
    this.upsellProductContainer = this.popup.querySelector('[data-upsell-product]');
    this.alreadyAddedMessage = this.popup.querySelector('[data-already-added]');

    this.currentSlideIndex = 0;
    this.autoRotateTimer = null;
    this.slideDuration = 10000; // auto-rotate interval (ms)

    this.setupEventListeners();
  }

  readConfig() {
    const node = document.querySelector('[data-cart-upsell-config]');
    if (!node) return { slides: [], oral: { enabled: false }, strings: {} };

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[upsell-popup] Invalid configuration JSON:', error);
      return { slides: [], oral: { enabled: false }, strings: {} };
    }
  }

  setupEventListeners() {
    // Close buttons
    this.popup.querySelectorAll('[data-close-upsell]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    });

    // Prevent modal clicks from closing
    const modal = this.popup.querySelector('.cart-upsell-popup__modal');
    if (modal) {
      modal.addEventListener('click', (e) => e.stopPropagation());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.popup.hidden) {
        this.close();
      }
    });

    // Listen for cart updates from product form.
    // Debounce so a burst of events results in a single popup open
    // instead of repeated rebuilds.
    document.addEventListener('cart:item-added', (e) => {
      this._latestAddedDetail = e.detail;
      clearTimeout(this._addedDebounce);
      this._addedDebounce = setTimeout(() => {
        this.handleProductAdded(this._latestAddedDetail);
      }, 200);
    });
  }

  async handleProductAdded(detail) {
    // Get fresh cart data to get all items with correct info
    const cart = await this.getCart();

    // Get newly added items
    const newItems = detail && detail.items ? detail.items : [];
    let itemsToDisplay = newItems;
    if (newItems.length === 0 && cart.items) {
      itemsToDisplay = cart.items.slice(-3);
    }

    // Build the ordered list of upsell slides
    await this.buildSlides(newItems, cart);

    // Display added items
    this.displayAddedProducts(itemsToDisplay);

    // Nothing to upsell -> don't open an empty popup
    if (!this.slides || this.slides.length === 0) return;

    // Show first slide and start auto-rotate (dots also allow manual nav)
    this.currentSlideIndex = 0;
    this.showSlide(0);
    this.startAutoRotate();

    this.open();
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('[upsell-popup] Error fetching cart:', error);
      return { items: [] };
    }
  }

  displayAddedProducts(items) {
    if (!items || items.length === 0 || !this.addedProductContainer) return;

    const quantityLabel = this.strings.quantity || 'Menge';

    const itemsHtml = items
      .map((item) => {
        const imageUrl = (item.featured_image && item.featured_image.url) || item.image || '';
        const title = item.product_title || item.title || 'Produkt';
        const variantTitle = item.variant_title || '';
        const fullTitle = variantTitle ? `${title} - ${variantTitle}` : title;

        // Check if there's a discount applied (final_price vs original_price)
        const originalPrice = item.original_price || item.price;
        const finalPrice = item.final_price || item.price;
        const hasDiscount = finalPrice < originalPrice;

        const priceHtml = hasDiscount
          ? `
            <p class="cart-upsell-popup__added-price">
              ${quantityLabel}: ${item.quantity} ×
              <span style="text-decoration: line-through; opacity: 0.6; margin-right: 4px;">${this.formatPrice(originalPrice)}</span>
              <span style="color: #E6007E; font-weight: 600;">${this.formatPrice(finalPrice)}</span>
            </p>`
          : `<p class="cart-upsell-popup__added-price">${quantityLabel}: ${item.quantity} × ${this.formatPrice(finalPrice)}</p>`;

        return `
          <div style="display: flex; gap: 12px; align-items: center; padding: 8px 0;">
            ${imageUrl ? `<img src="${imageUrl}" alt="${fullTitle}" class="cart-upsell-popup__added-image">` : ''}
            <div class="cart-upsell-popup__added-info">
              <h4 class="cart-upsell-popup__added-title">${fullTitle}</h4>
              ${priceHtml}
            </div>
          </div>
        `;
      })
      .join('');

    this.addedProductContainer.innerHTML = itemsHtml;
  }

  displaySlide(slide) {
    if (!slide || !this.upsellProductContainer) return;

    // Store current slide for addUpsellToCart
    this.currentSlide = slide;

    let imageUrl = slide.image || '';
    if (imageUrl && imageUrl.startsWith('//')) imageUrl = 'https:' + imageUrl;

    // Price (slide.discountPct is display-only; Shopify applies the real discount)
    const basePrice = slide.price || 0;
    const compareAtPrice = slide.compareAtPrice || 0;
    let oldPriceCents = 0;
    let newPriceCents = basePrice;
    let discountPercent = 0;

    if (slide.discountPct > 0) {
      oldPriceCents = basePrice;
      newPriceCents = Math.round(basePrice * (1 - slide.discountPct));
      discountPercent = Math.round(slide.discountPct * 100);
    } else if (compareAtPrice > basePrice) {
      oldPriceCents = compareAtPrice;
      newPriceCents = basePrice;
      discountPercent = Math.round(((compareAtPrice - basePrice) / compareAtPrice) * 100);
    }
    const hasDiscount = oldPriceCents > newPriceCents;

    const priceHtml = hasDiscount
      ? `
        <div class="cart-upsell-popup__product-price">
          <span class="cart-upsell-popup__product-price-old">${this.formatPrice(oldPriceCents)}</span>
          <span class="cart-upsell-popup__product-price-new">${this.formatPrice(newPriceCents)}</span>
        </div>`
      : `
        <div class="cart-upsell-popup__product-price">
          <span class="cart-upsell-popup__product-price-new">${this.formatPrice(newPriceCents)}</span>
        </div>`;

    const badgeText = slide.badgeLabel || '';
    const badgeClass = `cart-upsell-popup__badge--${slide.badgeStyle || 'new'}`;

    let description = slide.description || '';
    if (description && slide.truncateDesc && description.length > 100) {
      description = description.substring(0, 100) + '...';
    }
    const shortDescription = slide.shortDescription || '';
    const productUrl = slide.url || `/products/${slide.handle}`;

    const addLabel = hasDiscount
      ? (this.strings.addToCartDiscount || 'Mit [percent] % Rabatt hinzufügen').replace('[percent]', discountPercent)
      : this.strings.addToCart || 'In den Warenkorb legen';

    this.upsellProductContainer.innerHTML = `
      <div class="cart-upsell-popup__product-content">
        ${imageUrl ? `<a href="${productUrl}"><img src="${imageUrl}" alt="${slide.title}" class="cart-upsell-popup__product-image"></a>` : ''}
        <div class="cart-upsell-popup__product-info">
          ${badgeText ? `<span class="cart-upsell-popup__badge ${badgeClass}">${badgeText}</span>` : ''}
          <h4 class="cart-upsell-popup__product-title">
            <a href="${productUrl}">${slide.title}</a>
          </h4>
          ${description ? `<p class="cart-upsell-popup__product-desc">${description}</p>` : ''}
          ${shortDescription ? `<p class="cart-upsell-popup__product-short-desc">${shortDescription}</p>` : ''}
          ${priceHtml}
        </div>
      </div>
      <button class="cart-upsell-popup__btn cart-upsell-popup__btn--primary cart-upsell-popup__btn--upsell" data-add-upsell>
        ${addLabel}
      </button>
      ${this.buildDotsHtml()}
    `;

    const newAddBtn = this.upsellProductContainer.querySelector('[data-add-upsell]');
    if (newAddBtn) {
      newAddBtn.addEventListener('click', () => this.addUpsellToCart());
    }

    // Attach dot navigation handlers (manual nav restarts the auto-rotate timer)
    this.upsellProductContainer.querySelectorAll('[data-dot-index]').forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.dotIndex, 10);
        if (Number.isNaN(idx)) return;
        this.showSlide(idx);
        this.startAutoRotate();
      });
    });
  }

  startAutoRotate() {
    this.stopAutoRotate();
    if (!this.slides || this.slides.length <= 1) return;
    this.autoRotateTimer = setInterval(() => {
      const next = (this.currentSlideIndex + 1) % this.slides.length;
      this.showSlide(next);
    }, this.slideDuration);
  }

  stopAutoRotate() {
    if (this.autoRotateTimer) {
      clearInterval(this.autoRotateTimer);
      this.autoRotateTimer = null;
    }
  }

  buildDotsHtml() {
    if (!this.slides || this.slides.length <= 1) return '';
    const template = this.strings.slideAriaLabel || 'Angebot [number] anzeigen';
    const dots = this.slides
      .map((_, i) => {
        const active = i === this.currentSlideIndex ? ' is-active' : '';
        const label = template.replace('[number]', i + 1);
        return `<button type="button" class="cart-upsell-popup__dot${active}" data-dot-index="${i}" aria-label="${label}"></button>`;
      })
      .join('');
    return `<div class="cart-upsell-popup__dots">${dots}</div>`;
  }

  async addUpsellToCart() {
    const btn = this.upsellProductContainer.querySelector('[data-add-upsell]');
    if (!this.currentSlide || !this.currentSlide.variantId || !btn || btn.disabled) return;

    btn.disabled = true;
    btn.textContent = this.strings.adding || 'Wird hinzugefügt ...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: this.currentSlide.variantId, quantity: 1 }],
        }),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      this.updateCartCount();

      btn.textContent = this.strings.added || '✓ Hinzugefügt!';

      setTimeout(() => {
        window.location.href = '/cart';
      }, 500);
    } catch (error) {
      console.error('[upsell-popup] Error adding upsell to cart:', error);
      btn.textContent = this.strings.retry || 'Erneut versuchen';
      btn.disabled = false;
    }
  }

  updateCartCount() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        const cartBubble = document.querySelector('.cart-count-bubble');
        if (cartBubble) {
          cartBubble.textContent = cart.item_count;
          if (cart.item_count > 0) {
            cartBubble.classList.remove('hidden');
          }
        }
      });
  }

  formatPrice(cents) {
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return `${euros} €`;
  }

  stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  open() {
    this.popup.hidden = false;
    this.popup.setAttribute('aria-hidden', 'false');
    // Don't lock body scroll - allow scrolling behind popup
  }

  close() {
    this.stopAutoRotate();
    this.popup.hidden = true;
    this.popup.setAttribute('aria-hidden', 'true');
  }

  async fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        console.warn(`[upsell-popup] Skipping ${handle}: HTTP ${response.status}`);
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error(`[upsell-popup] Error loading product ${handle}:`, error);
      return null;
    }
  }

  isOralVariant(item) {
    const title = (item.variant_title || '').toLowerCase();
    if (title.includes('oral')) return true;
    const opts = item.options_with_values || [];
    return opts.some((o) => String((o && o.value) || '').toLowerCase().includes('oral'));
  }

  async buildOralSlide(addedItems, cartVariantIds) {
    const oralConfig = this.config.oral || {};

    // Offer the oral variant of a just-added test product (same gender)
    for (const item of addedItems) {
      if (!item.handle) continue;
      const product = await this.fetchProduct(item.handle);
      if (!product) continue;

      const addedVariant = product.variants.find((v) => v.id === (item.variant_id || item.id));
      const gender = addedVariant ? (addedVariant.option1 || '').toLowerCase() : '';

      const oralVariant = product.variants.find((v) => {
        if (v.available === false) return false;
        const opts = [v.option1, v.option2, v.option3].filter(Boolean).map((o) => o.toLowerCase());
        const isOral = opts.some((o) => o.includes('oral'));
        const matchesGender = gender ? opts.includes(gender) : true;
        return isOral && matchesGender;
      });

      if (oralVariant && !cartVariantIds.has(oralVariant.id)) {
        let image = product.featured_image || '';
        if (oralVariant.featured_image) {
          image = oralVariant.featured_image.src || oralVariant.featured_image;
        }

        return {
          handle: product.handle,
          url: `/products/${product.handle}`,
          variantId: oralVariant.id,
          price: oralVariant.price,
          compareAtPrice: oralVariant.compare_at_price || 0,
          image,
          title: oralConfig.title || product.title,
          description: oralConfig.description || '',
          shortDescription: oralConfig.shortDescription || '',
          badgeLabel: oralConfig.badgeLabel || '',
          badgeStyle: oralConfig.badgeStyle || 'medical',
          truncateDesc: false,
          discountPct: 0,
        };
      }
    }
    return null;
  }

  async buildSlides(addedItems, cart) {
    this.slides = [];
    if (this.alreadyAddedMessage) this.alreadyAddedMessage.hidden = true;

    const cartHandles = new Set((cart.items || []).map((i) => i.handle));
    const cartVariantIds = new Set((cart.items || []).map((i) => i.variant_id || i.id));

    // 1. Oral upsell when NO oral variant was just added
    const oralConfig = this.config.oral || {};
    const addedHasOral = (addedItems || []).some((it) => this.isOralVariant(it));
    if (oralConfig.enabled && !addedHasOral) {
      const oralSlide = await this.buildOralSlide(addedItems || [], cartVariantIds);
      if (oralSlide) this.slides.push(oralSlide);
    }

    // 2. Configured products (skip anything already in the cart or without a buyable variant)
    for (const slide of this.config.slides || []) {
      if (!slide.variantId) continue;
      if (cartHandles.has(slide.handle)) continue;
      this.slides.push({ ...slide, truncateDesc: false, discountPct: 0 });
    }
  }

  showSlide(index) {
    if (!this.slides || this.slides.length === 0) return;
    this.currentSlideIndex = index;
    this.displaySlide(this.slides[index]);
  }
}

// Initialize when DOM is ready (singleton: avoid multiple instances/listeners)
function initCartUpsellPopup() {
  if (window.cartUpsellPopupInstance) return;
  window.cartUpsellPopupInstance = new CartUpsellPopup();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartUpsellPopup);
} else {
  initCartUpsellPopup();
}
